import { Card } from "antd";
import { X_AXIS_KEY } from "components/Graph/constants";
import { useHiddenLinesLocalStorage } from "components/Graph/hooks/useHiddenLinesLocalStorage";
import { useHighlights } from "components/Graph/hooks/useHighlights";
import { formatKeyValue } from "components/Graph/hooks/useKeyFormatter";
import { GraphId, NestedPaths } from "components/Graph/types";
import { Highlights } from "components/Graph/UI/Highlights/Highlights";
import { GraphStateRenderer } from "components/Graph/UI/StateRenderers/GraphStateRenderer";
import { EntityGraphUtils } from "components/Graph/utils/EntityGraphUtils";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils";
import dayjs from "dayjs";
import { useCurrencies } from "hooks/useCurrencies";
import { usePermission } from "hooks/usePermissions";
import { DATE_FORMAT } from "localConstants";
import { useMemo, useState, useCallback, useRef } from "react";
import { Area, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PermissionEnum } from "types/enums";
import { getColorByIndex } from "utils/getColorByIndex";

interface Props<TData extends object, CPath extends keyof TData> {
    data: TData[];
    keys: CPath[];
    defaultVisibleKeys: CPath[];
    defaultActiveKey: CPath;
    graphId: GraphId;
    entity: CPath;
    entityId: NestedPaths<TData>;
    onActiveHighlightChange?: (key: CPath) => void;
    loading?: boolean;
    error?: boolean;
    isSelectionOn?: boolean;
    selectedEntities?: string[];
    defaultSelectedEntities?: string[];
    summaryData?: object;
}

export const EntityGraph = <TData extends object, CPath extends keyof TData>({
    data,
    keys,
    defaultVisibleKeys,
    defaultActiveKey,
    graphId,
    entity,
    entityId,
    loading,
    error,
    onActiveHighlightChange,
    isSelectionOn,
    defaultSelectedEntities = [],
    selectedEntities = [],
    summaryData,
}: Props<TData, CPath>) => {
    const { get } = useCurrencies();
    const canViewGraph = usePermission().hasPermission(PermissionEnum.ReportShowInGraph);

    const {
        highlights,
        handleClick,
        handleHide,
        handleAdd,
        activeHighlights: activeArray,
    } = useHighlights({
        data,
        keys,
        graphId,
        defaultActiveKeys: [defaultActiveKey],
        defaultVisibleKeys,
        onActiveHighlightChange,
    });

    const [activeHighlight] = activeArray;
    const utils = useMemo(() => new EntityGraphUtils(data, activeHighlight), [data, activeHighlight]);

    // Use selectedEntities if selection is on AND there are selections, otherwise fall back to defaults
    const effectiveEntities = isSelectionOn && selectedEntities.length > 0 ? selectedEntities : defaultSelectedEntities;

    const { graphData, uniqueEntities, labelLookup } = utils.getGraphData(entity, entityId, effectiveEntities);

    const highlightsData = summaryData ? [summaryData as TData] : utils.getHighlightsData(entity, effectiveEntities);

    const { get: getHiddenLines, set: setHiddenLinesStorage } = useHiddenLinesLocalStorage();
    const [hiddenLines, setHiddenLines] = useState<Set<string>>(() => new Set(getHiddenLines(graphId)));

    const lastClickTime = useRef<number>(0);

    const handleLegendClick = useCallback(
        (dataKey: string) => {
            const now = Date.now();
            // Throttle clicks to max once per 100ms
            if (now - lastClickTime.current < 100) {
                return;
            }
            lastClickTime.current = now;

            setHiddenLines((prev) => {
                const next = new Set(prev);
                if (next.has(dataKey)) {
                    next.delete(dataKey);
                } else {
                    next.add(dataKey);
                }
                // Use setTimeout to defer localStorage operation to avoid blocking
                setTimeout(() => {
                    setHiddenLinesStorage(Array.from(next), graphId);
                }, 0);
                return next;
            });
        },
        [setHiddenLinesStorage, graphId]
    );

    const labelFormatter = (value: string | number, name: string) => {
        const rawValueType = GeneralGraphUtils.getMetricType(data, activeHighlight, graphId);
        const formattedValue = formatKeyValue(value, rawValueType, get);

        const formattedName = labelLookup.get(name) || String(name);

        return [formattedValue, formattedName];
    };

    const isEmpty = !loading && !error && graphData.length === 0;

    return (
        <Card>
            <Highlights
                graphId={graphId}
                isLoading={!!loading || !!error}
                active={activeArray}
                data={highlightsData}
                handleAdd={(key) => handleAdd(key)}
            >
                {highlights.map(({ key, color, state, hidden }) => (
                    <Highlights.Item
                        color={color}
                        state={state}
                        hidden={hidden}
                        metricKey={String(key)}
                        onHide={() => handleHide(key)}
                        onClick={() => handleClick(key)}
                        key={key as string}
                    />
                ))}
            </Highlights>
            {canViewGraph && (
                <div className="w-full overflow-x-auto overflow-y-hidden">
                    <div className="min-w-[700px] h-96">
                        {loading || error || isEmpty ? (
                            <GraphStateRenderer empty={isEmpty} error={!!error} loading={!!loading} />
                        ) : (
                            <ResponsiveContainer>
                                <ComposedChart data={graphData} margin={{ top: 20, right: 100, left: 20, bottom: 10 }}>
                                    <defs>
                                        {uniqueEntities.slice(0, 10).map((offer, idx) => (
                                            <linearGradient key={offer} id={`gradient-entity-${idx}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={getColorByIndex(idx)} stopOpacity={0.3} />
                                                <stop offset="100%" stopColor={getColorByIndex(idx)} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <XAxis
                                        axisLine={false}
                                        dataKey={X_AXIS_KEY}
                                        tickFormatter={(str) => dayjs(str).format(DATE_FORMAT)}
                                    />
                                    <YAxis axisLine={false} />
                                    <CartesianGrid
                                        vertical={false}
                                        strokeDasharray="12"
                                        opacity={0.4}
                                    />
                                    <Tooltip
                                        formatter={labelFormatter}
                                        labelFormatter={(str) => dayjs(str).format(DATE_FORMAT)}
                                    />

                                    <Legend
                                        align="left"
                                        verticalAlign="bottom"
                                        layout="horizontal"
                                        onClick={(data) => {
                                            if (data && data.dataKey) {
                                                handleLegendClick(String(data.dataKey));
                                            }
                                        }}
                                        wrapperStyle={{
                                            paddingLeft: 20,
                                            bottom: 0,
                                            cursor: "pointer",
                                        }}
                                        formatter={(value) => {
                                            const dataKey = String(value);
                                            const label = labelLookup.get(dataKey) || dataKey;
                                            const isHidden = hiddenLines.has(dataKey);
                                            return (
                                                <span
                                                    key={dataKey}
                                                    style={{
                                                        color: isHidden ? "#ccc" : "#000",
                                                        textDecoration: isHidden ? "line-through" : undefined,
                                                        marginRight: "12px",
                                                    }}
                                                >
                                                    {label}
                                                </span>
                                            );
                                        }}
                                        iconType="circle"
                                    />

                                    {uniqueEntities.slice(0, 10).map((offer, idx) => {
                                        const isHidden = hiddenLines.has(offer);
                                        const color = getColorByIndex(idx);
                                        return (
                                            <Area
                                                key={offer}
                                                type={"monotone"}
                                                name={offer}
                                                dataKey={offer}
                                                strokeWidth={isHidden ? 0 : 2}
                                                stroke={color}
                                                fill={`url(#gradient-entity-${idx})`}
                                                fillOpacity={isHidden ? 0 : 1}
                                                activeDot={false}
                                                dot={false}
                                                hide={isHidden}
                                            />
                                        );
                                    })}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};
